import { useCallback, useState, useMemo, useEffect, useRef } from "react";
import { createViewCompanies, createViewUsers, createViewAnnouncements } from "@/ui/views";
import { NOOP } from "@/utils/utils";
import type { TAsyncState } from "@/store";
import type { TApiCompanyFeatureFlags } from "@/apis/proda_finance/types";
import type { TCompositionDeps } from "./types";

export const wireAdmin = ({ store, useAppStore, api }: TCompositionDeps) => {
    // ==========================================
    // View: Companies (Admin Only)
    // ==========================================
    const ViewCompanies = createViewCompanies({
        useCases: () => {
            const PAGE_SIZE = 100;
            const [total, setTotal] = useState(0);
            const [companiesTotalUnfiltered, setCompaniesTotalUnfiltered] = useState(0);

            const [companiesState, setCompaniesState] = useState<{
                status: "idle" | "pending" | "fulfilled" | "rejected";
                data: Array<{
                    id: string;
                    name: string;
                    slug: string;
                    code: string;
                    emailDomains: string[];
                    contactEmail: string | null;
                    contactName: string | null;
                    isActive: boolean;
                    hasLogo: boolean;
                    logoUrl: string | null;
                    featureFlags: TApiCompanyFeatureFlags;
                    createdAt: string;
                    updatedAt: string;
                }> | null;
                error: Error | null;
            }>({ status: "idle", data: null, error: null });

            const loadCompanies = useCallback(
                (
                    query: {
                        skip?: number;
                        limit?: number;
                        name?: string;
                        code?: string;
                        contact?: string;
                        email_domains?: string;
                        is_active?: boolean;
                        sort_by?: string;
                        sort_dir?: string;
                    },
                    signal?: AbortSignal,
                ) => {
                    setCompaniesState({ status: "pending", data: null, error: null });
                    api.companies
                        .list(query, signal)
                        .then((response) => {
                            const companies = response.items.map((c) => ({
                                id: c.id,
                                name: c.name,
                                slug: c.slug,
                                code: c.code,
                                emailDomains: c.email_domains,
                                contactEmail: c.contact_email,
                                contactName: c.contact_name,
                                isActive: c.is_active,
                                hasLogo: c.has_logo,
                                logoUrl: c.logo_url,
                                featureFlags: c.feature_flags ?? {},
                                createdAt: c.created_at,
                                updatedAt: c.updated_at,
                            }));
                            setTotal(response.total);
                            setCompaniesTotalUnfiltered(
                                response.total_unfiltered ?? response.total,
                            );
                            setCompaniesState({
                                status: "fulfilled",
                                data: companies,
                                error: null,
                            });

                            store.getState().setAvailableCompanies(
                                companies.map((c) => ({
                                    id: c.id,
                                    name: c.name,
                                    slug: c.slug,
                                    code: c.code,
                                    isActive: c.isActive,
                                    logoUrl: c.logoUrl,
                                })),
                            );
                        })
                        .catch((error: unknown) => {
                            // Swallow abort errors — the superseding request owns the loading state.
                            if (error instanceof DOMException && error.name === "AbortError") {
                                return;
                            }
                            setCompaniesState({
                                status: "rejected",
                                data: null,
                                error: error instanceof Error ? error : new Error(String(error)),
                            });
                        });
                },
                [],
            );

            // After mutations we reload with the same query. Keep track of the last
            // query issued so post-mutation reloads use the current filters/sort.
            const lastQueryRef = useRef<Parameters<typeof loadCompanies>[0]>({
                skip: 0,
                limit: PAGE_SIZE,
            });
            const loadCompaniesTracked = useCallback(
                (query: Parameters<typeof loadCompanies>[0], signal?: AbortSignal) => {
                    lastQueryRef.current = query;
                    loadCompanies(query, signal);
                },
                [loadCompanies],
            );

            const reloadCurrent = useCallback(() => {
                loadCompanies(lastQueryRef.current);
            }, [loadCompanies]);

            const onCreateCompany = useCallback(
                async (data: {
                    name: string;
                    slug: string;
                    code: string;
                    emailDomains?: string[];
                    contactEmail?: string | null;
                    contactName?: string | null;
                }): Promise<string> => {
                    const company = await api.companies.create({
                        name: data.name,
                        slug: data.slug,
                        code: data.code,
                        email_domains: data.emailDomains,
                        contact_email: data.contactEmail,
                        contact_name: data.contactName,
                    });
                    reloadCurrent();
                    return company.id;
                },
                [reloadCurrent],
            );

            const onUpdateCompany = useCallback(
                async (
                    id: string,
                    data: {
                        name?: string;
                        slug?: string;
                        code?: string;
                        emailDomains?: string[];
                        contactEmail?: string | null;
                        contactName?: string | null;
                        isActive?: boolean;
                    },
                ) => {
                    await api.companies.update(id, {
                        name: data.name,
                        slug: data.slug,
                        code: data.code,
                        email_domains: data.emailDomains,
                        contact_email: data.contactEmail,
                        contact_name: data.contactName,
                        is_active: data.isActive,
                    });
                    reloadCurrent();
                },
                [reloadCurrent],
            );

            const onUpdateCompanyConfig = useCallback(
                async (
                    id: string,
                    data: {
                        emailCommunicationsEnabled: boolean;
                        homepageV2Enabled: boolean;
                        operatingStatementEnabled: boolean;
                    },
                ) => {
                    await api.companies.updateConfig(id, {
                        email_communications_enabled: data.emailCommunicationsEnabled,
                        homepage_v2_enabled: data.homepageV2Enabled,
                        operating_statement_enabled: data.operatingStatementEnabled,
                    });
                    reloadCurrent();
                },
                [reloadCurrent],
            );

            const onDeleteCompany = useCallback(
                async (id: string) => {
                    await api.companies.delete(id);
                    reloadCurrent();
                },
                [reloadCurrent],
            );

            const onUploadLogo = useCallback(
                async (id: string, logo: File) => {
                    await api.companies.uploadLogo(id, logo);
                    reloadCurrent();
                },
                [reloadCurrent],
            );

            const onDeleteLogo = useCallback(
                async (id: string) => {
                    await api.companies.deleteLogo(id);
                    reloadCurrent();
                },
                [reloadCurrent],
            );

            return {
                onMount: NOOP,
                onUnmount: NOOP,
                useCompaniesState: () =>
                    companiesState as TAsyncState<(typeof companiesState)["data"]>,
                usePagination: () => ({ page: 1, pageSize: PAGE_SIZE, total }),
                onPageChange: NOOP,
                loadCompanies: loadCompaniesTracked,
                useCompaniesTotalUnfiltered: () => companiesTotalUnfiltered,
                onCreateCompany,
                onUpdateCompany,
                onUpdateCompanyConfig,
                onDeleteCompany,
                onUploadLogo,
                onDeleteLogo,
                onRefresh: reloadCurrent,
            };
        },
    });

    // ==========================================
    // View: Users (Admin Only)
    // ==========================================
    const ViewUsers = createViewUsers({
        useCases: () => {
            const effectiveCompanyId = useAppStore((state) => state.effectiveCompanyId);
            const availableCompanies = useAppStore((state) => state.availableCompanies);
            const currentUser = useAppStore((state) => state.user);

            const USERS_PAGE_SIZE = 100;
            const [usersCurrentPage, setUsersCurrentPage] = useState(1);
            const [usersTotal, setUsersTotal] = useState(0);
            const [usersTotalUnfiltered, setUsersTotalUnfiltered] = useState(0);

            const [usersState, setUsersState] = useState<{
                status: "idle" | "pending" | "fulfilled" | "rejected";
                data: Array<{
                    id: string;
                    companyId: string;
                    companyName: string;
                    companyCode: string;
                    email: string;
                    displayName: string;
                    role: string;
                    subgroup: string | null;
                    roleLabel?: string;
                    subgroupLabel?: string | null;
                    isActive: boolean;
                    lastLoginAt: string | null;
                    createdAt: string;
                    updatedAt: string;
                }> | null;
                error: Error | null;
            }>({ status: "idle", data: null, error: null });

            const getCompanyInfo = useCallback(
                (companyId: string) => {
                    const company = availableCompanies.find((c) => c.id === companyId);
                    return {
                        companyName: company?.name || "Unknown",
                        companyCode: company?.code || "UNK",
                    };
                },
                [availableCompanies],
            );

            const isProdaUser = useCallback(() => {
                if (!currentUser) return false;
                const userCompanyCode = availableCompanies.find(
                    (c) => c.id === currentUser.companyId,
                )?.code;
                return userCompanyCode?.toUpperCase() === "PRODA";
            }, [currentUser, availableCompanies]);

            const isProdaAdmin = useCallback(() => {
                if (!currentUser) return false;
                const userCompanyCode = availableCompanies.find(
                    (c) => c.id === currentUser.companyId,
                )?.code;
                return currentUser.role === "admin" && userCompanyCode?.toUpperCase() === "PRODA";
            }, [currentUser, availableCompanies]);

            const canAssignSubgroup = useCallback(() => {
                return currentUser?.permissions?.includes("user_assign_subgroup") ?? false;
            }, [currentUser]);

            const hasUserCreatePermission = useCallback(() => {
                return currentUser?.permissions?.includes("user_create") ?? false;
            }, [currentUser]);

            const hasUserEditPermission = useCallback(() => {
                return currentUser?.permissions?.includes("user_edit") ?? false;
            }, [currentUser]);

            const hasUserDeletePermission = useCallback(() => {
                return currentUser?.permissions?.includes("user_delete") ?? false;
            }, [currentUser]);

            const isViewingAsOtherCompany = useCallback(() => {
                if (!effectiveCompanyId) return false;
                return effectiveCompanyId !== currentUser?.companyId;
            }, [effectiveCompanyId, currentUser?.companyId]);

            const loadUsers = useCallback(
                (
                    query: {
                        page: number;
                        page_size: number;
                        search?: string;
                        is_active?: boolean;
                        company_id?: string;
                        sort_by?: string;
                        sort_dir?: string;
                    },
                    signal?: AbortSignal,
                ) => {
                    setUsersCurrentPage(query.page);
                    setUsersState({ status: "pending", data: null, error: null });
                    api.users
                        .list(query, signal)
                        .then((response) => {
                            setUsersTotal(response.total);
                            setUsersTotalUnfiltered(response.total_unfiltered ?? response.total);
                            setUsersState({
                                status: "fulfilled",
                                data: response.items.map((u) => {
                                    const { companyName, companyCode } = getCompanyInfo(
                                        u.company_id,
                                    );
                                    return {
                                        id: u.id,
                                        companyId: u.company_id,
                                        companyName,
                                        companyCode,
                                        email: u.email,
                                        displayName: u.display_name,
                                        role: u.role,
                                        subgroup: u.subgroup,
                                        roleLabel: u.role_label,
                                        subgroupLabel: u.subgroup_label,
                                        isActive: u.is_active,
                                        lastLoginAt: u.last_login_at,
                                        createdAt: u.created_at,
                                        updatedAt: u.updated_at,
                                    };
                                }),
                                error: null,
                            });
                        })
                        .catch((error: unknown) => {
                            // Swallow abort errors — the superseding request owns the loading state.
                            if (error instanceof DOMException && error.name === "AbortError") {
                                return;
                            }
                            console.error("Failed to load users:", error);
                            let errorMessage = "Failed to load users";
                            if (error instanceof Error) {
                                errorMessage = error.message;
                            } else if (
                                typeof error === "object" &&
                                error !== null &&
                                "detail" in error
                            ) {
                                errorMessage = String((error as { detail: unknown }).detail);
                            }
                            setUsersState({
                                status: "rejected",
                                data: null,
                                error: new Error(errorMessage),
                            });
                        });
                },
                // eslint-disable-next-line react-hooks/exhaustive-deps
                [effectiveCompanyId, getCompanyInfo],
            );

            const onUsersPageChange = useCallback(
                (page: number) => {
                    setUsersCurrentPage(page);
                    loadUsers({ page, page_size: USERS_PAGE_SIZE });
                },
                [loadUsers],
            );

            const onCreateUser = useCallback(
                async (data: {
                    companyId: string;
                    email: string;
                    displayName: string;
                    role: string;
                    subgroup?: string | null;
                    isActive?: boolean;
                }) => {
                    await api.users.create({
                        company_id: data.companyId,
                        email: data.email,
                        display_name: data.displayName,
                        role: data.role,
                        subgroup: data.subgroup ?? undefined,
                        is_active: data.isActive,
                    });
                    // The list is refreshed by the view via the server table's
                    // refetch(), which preserves the active search/filter/sort.
                },
                [],
            );

            const onUpdateUser = useCallback(
                async (
                    id: string,
                    data: {
                        companyId?: string;
                        displayName?: string;
                        role?: string;
                        subgroup?: string;
                        isActive?: boolean;
                    },
                ) => {
                    await api.users.update(id, {
                        company_id: data.companyId,
                        display_name: data.displayName,
                        role: data.role,
                        subgroup: data.subgroup,
                        is_active: data.isActive,
                    });
                    // The list is refreshed by the view via the server table's
                    // refetch(), which preserves the active search/filter/sort/page.
                },
                [],
            );

            const onDeleteUser = useCallback(async (id: string) => {
                await api.users.delete(id);
                // The list is refreshed by the view via the server table's
                // refetch(), which preserves the active search/filter/sort/page.
            }, []);

            // Per-user feature flags — Proda users only (the API answers 403
            // to anyone else, and the view never offers the button to them).
            const loadUserFeatureFlags = useCallback(async (id: string) => {
                const flags = await api.users.getFeatureFlags(id);
                return {
                    overrides: flags.overrides,
                    companyFlags: flags.company_flags,
                    effectiveFlags: flags.effective_flags,
                };
            }, []);

            const onUpdateUserFeatureFlags = useCallback(
                async (id: string, overrides: Record<string, boolean | null>) => {
                    await api.users.updateFeatureFlags(id, { overrides });
                },
                [],
            );

            const shouldExposeCompaniesForUserManagement =
                isProdaUser() && !isViewingAsOtherCompany();

            const getEffectiveCompanyId = useCallback(() => {
                return effectiveCompanyId || currentUser?.companyId || null;
            }, [effectiveCompanyId, currentUser?.companyId]);

            return {
                onMount: NOOP,
                onUnmount: NOOP,
                useUsersState: () => usersState as TAsyncState<(typeof usersState)["data"]>,
                usePagination: () => ({
                    page: usersCurrentPage,
                    pageSize: USERS_PAGE_SIZE,
                    total: usersTotal,
                }),
                onPageChange: onUsersPageChange,
                loadUsers,
                useUsersTotalUnfiltered: () => usersTotalUnfiltered,
                useAvailableCompanies: () =>
                    shouldExposeCompaniesForUserManagement
                        ? availableCompanies.map((c) => ({
                              id: c.id,
                              name: c.name,
                              code: c.code,
                          }))
                        : [],
                getCurrentUserId: () => currentUser?.id || null,
                getEffectiveCompanyId,
                isProdaUser,
                isProdaAdmin,
                isViewingAsOtherCompany,
                canAssignSubgroup,
                hasUserCreatePermission,
                hasUserEditPermission,
                hasUserDeletePermission,
                // Role/subgroup labels and the role->subgroups partition come from
                // /auth/me metadata (single source of truth), not frontend constants.
                getRoleMetadata: () => {
                    const subgroupRoles = currentUser?.subgroupRoles ?? {};
                    const roleLabels = currentUser?.roleLabels ?? {};
                    const subgroupLabels = currentUser?.subgroupLabels ?? {};
                    const roleSubgroups: Record<string, string[]> = {};
                    for (const [sg, role] of Object.entries(subgroupRoles)) {
                        (roleSubgroups[role] ??= []).push(sg);
                    }
                    const roleOptions = Object.keys(roleLabels);
                    const roleDefaultSubgroup: Record<string, string> = {};
                    for (const [role, sgs] of Object.entries(roleSubgroups)) {
                        if (sgs[0]) roleDefaultSubgroup[role] = sgs[0];
                    }
                    return {
                        roleOptions,
                        roleLabels,
                        subgroupLabels,
                        roleSubgroups,
                        roleDefaultSubgroup,
                        assignableSubgroups: currentUser?.assignableSubgroups ?? [],
                    };
                },
                onCreateUser,
                onUpdateUser,
                onDeleteUser,
                loadUserFeatureFlags,
                onUpdateUserFeatureFlags,
            };
        },
    });

    // ==========================================
    // View: Announcements (Admin Only)
    // ==========================================
    const ViewAnnouncements = createViewAnnouncements({
        useCases: () => {
            const [announcementsState, setAnnouncementsState] = useState<{
                status: "idle" | "pending" | "fulfilled" | "rejected";
                data: Array<{
                    id: string;
                    title: string;
                    message: string;
                    type: "info" | "warning";
                    targeting: {
                        all_companies: boolean;
                        company_ids: string[];
                        company_roles: Record<string, string[] | null>;
                    };
                    startAt: string;
                    endAt: string;
                    isActive: boolean;
                    createdAt: string;
                    updatedAt: string;
                }> | null;
                error: Error | null;
            }>({ status: "idle", data: null, error: null });

            const loadAnnouncements = useCallback(async () => {
                setAnnouncementsState({ status: "pending", data: null, error: null });
                try {
                    const response = await api.announcements.list();
                    setAnnouncementsState({
                        status: "fulfilled",
                        data: response.items.map((a) => ({
                            id: a.id,
                            title: a.title,
                            message: a.message,
                            type: a.type,
                            targeting: a.targeting,
                            startAt: a.start_at,
                            endAt: a.end_at,
                            isActive: a.is_active,
                            createdAt: a.created_at,
                            updatedAt: a.updated_at,
                        })),
                        error: null,
                    });
                } catch (error) {
                    console.error("Failed to load announcements:", error);
                    let errorMessage = "Failed to load announcements";
                    if (error instanceof Error) {
                        errorMessage = error.message;
                    } else if (typeof error === "object" && error !== null && "detail" in error) {
                        errorMessage = String((error as { detail: unknown }).detail);
                    }
                    setAnnouncementsState({
                        status: "rejected",
                        data: null,
                        error: new Error(errorMessage),
                    });
                }
            }, []);

            useEffect(() => {
                loadAnnouncements();
            }, [loadAnnouncements]);

            const onCreateAnnouncement = useCallback(
                async (data: {
                    title: string;
                    message: string;
                    type: "info" | "warning";
                    targeting: {
                        all_companies: boolean;
                        company_ids: string[];
                        company_roles: Record<string, string[] | null>;
                    };
                    startAt: string;
                    endAt: string;
                    isActive: boolean;
                }) => {
                    await api.announcements.create({
                        title: data.title,
                        message: data.message,
                        type: data.type,
                        targeting: data.targeting,
                        start_at: data.startAt,
                        end_at: data.endAt,
                        is_active: data.isActive,
                    });
                    await loadAnnouncements();
                },
                [loadAnnouncements],
            );

            const onUpdateAnnouncement = useCallback(
                async (
                    id: string,
                    data: {
                        title?: string;
                        message?: string;
                        type?: "info" | "warning";
                        targeting?: {
                            all_companies: boolean;
                            company_ids: string[];
                            company_roles: Record<string, string[] | null>;
                        };
                        startAt?: string;
                        endAt?: string;
                        isActive?: boolean;
                    },
                ) => {
                    await api.announcements.update(id, {
                        title: data.title,
                        message: data.message,
                        type: data.type,
                        targeting: data.targeting,
                        start_at: data.startAt,
                        end_at: data.endAt,
                        is_active: data.isActive,
                    });
                    await loadAnnouncements();
                },
                [loadAnnouncements],
            );

            const onDeleteAnnouncement = useCallback(
                async (id: string) => {
                    await api.announcements.delete(id);
                    await loadAnnouncements();
                },
                [loadAnnouncements],
            );

            const availableCompanies = useAppStore((state) => state.availableCompanies);
            const currentUser = useAppStore((state) => state.user);
            const effectiveCompanyId = useAppStore((state) => state.effectiveCompanyId);

            const effectiveCompanyName = useAppStore((state) => state.effectiveCompanyName);

            const isProdaAdminFlag = useMemo(() => {
                if (!currentUser?.isProdaUser || !currentUser?.isAdmin) return false;
                if (!effectiveCompanyId || effectiveCompanyId === currentUser.companyId)
                    return true;
                return false;
            }, [currentUser, effectiveCompanyId]);

            const announcementCompanies = useMemo(() => {
                if (isProdaAdminFlag) {
                    return availableCompanies.map((c) => ({
                        id: c.id,
                        name: c.name,
                        code: c.code,
                    }));
                }
                if (effectiveCompanyId && effectiveCompanyName) {
                    return [
                        {
                            id: effectiveCompanyId,
                            name: effectiveCompanyName,
                            code: "",
                        },
                    ];
                }
                if (currentUser?.companyId && currentUser?.companyName) {
                    return [
                        {
                            id: currentUser.companyId,
                            name: currentUser.companyName,
                            code: "",
                        },
                    ];
                }
                return [];
            }, [
                isProdaAdminFlag,
                availableCompanies,
                currentUser,
                effectiveCompanyId,
                effectiveCompanyName,
            ]);

            return {
                onMount: useCallback(() => {
                    loadAnnouncements();
                }, [loadAnnouncements]),
                onUnmount: NOOP,
                // Cast to TAsyncState to preserve discriminated union type
                useAnnouncementsState: () =>
                    announcementsState as TAsyncState<(typeof announcementsState)["data"]>,
                useAvailableCompanies: () => announcementCompanies,
                useIsProdaAdmin: () => isProdaAdminFlag,
                onCreateAnnouncement,
                onUpdateAnnouncement,
                onDeleteAnnouncement,
            };
        },
    });

    return { ViewCompanies, ViewUsers, ViewAnnouncements };
};
